import React, { useEffect, useRef } from 'react';
import { Input, ProviderConsumer, SearchIcon } from '@fluentui/react-northstar';
import { themeNames } from '@fluentui/react-teams';
import { useTranslation } from 'react-i18next';
import { appState, useSetState } from '../../AppState';
import {
  getChildFiltersCountFromStorage,
  getChildFiltersFromStorage,
  setChildFiltersInStorage
} from './CustomViewPopup/heplers/customPopupHelper';

/** How long typing pauses before the list searches - the user page's Tickets tab waits the same. */
export const SEARCH_PAUSE_MS = 400;

/**
 * The ticket list's text search (Id, Request or Comments), in the toolbar next to Filter rather than inside the
 * Filter panel - it narrows the same view as the view picker and the filters. It searches as you type, once
 * typing pauses, or at once on Enter, as the user page's Tickets tab does. The text stays in the shared filter
 * state (filterDropdown.queryValue), so the phone's toolbar search and this box are one search; applying it
 * keeps the view's applied filters as they are and only replaces the search text.
 */
export const ToolbarSearch = () => {
  const { t } = useTranslation();
  const state = appState();
  const setState = useSetState();
  const query = state.filterDropdown?.queryValue ?? '';

  // The applied text, read when a pause ends rather than when it starts.
  const appliedRef = useRef(state.filterDropdown?.queryValueApplied ?? '');
  appliedRef.current = state.filterDropdown?.queryValueApplied ?? '';

  const apply = async (value: string) => {
    const trimmed = value.trim();
    if (trimmed === appliedRef.current) return;
    appliedRef.current = trimmed;
    const [values, count] = await Promise.all([getChildFiltersFromStorage(), getChildFiltersCountFromStorage()]);
    // Not awaited: the in-memory copy it keeps is updated before it writes to IndexedDB, and that write can take
    // most of a second - the list reloads at once and the stored copy catches up for the next page load.
    setChildFiltersInStorage(values ?? state.filterDropdown?.selectedValues, count, trimmed);
    setState(prev => ({
      ...prev,
      filtersApplyCount: prev.filtersApplyCount + 1,
      filterDropdown: { ...prev.filterDropdown, queryValueApplied: trimmed }
    }));
  };

  useEffect(() => {
    const pause = window.setTimeout(() => apply(query), SEARCH_PAUSE_MS);
    return () => window.clearTimeout(pause);
  }, [query]);

  const placeholder = t('ticket.child-filter.search-request');
  return (
    <ProviderConsumer render={globalTheme => {
      const { colorScheme } = globalTheme.siteVariables;
      // At rest a light grey line in light mode - the text's grey read too dark there (Shaun, 2026-10-21); dark mode keeps it.
      const restLine = globalTheme.siteVariables.theme === themeNames.Dark ? colorScheme.default.foreground2 : colorScheme.default.borderHover;
      return (
        <Input
          clearable
          icon={<SearchIcon />}
          value={query}
          placeholder={placeholder}
          aria-label={placeholder}
          onChange={(_e, data) => {
            const value = data?.value ?? '';
            setState(prev => ({ ...prev, filterDropdown: { ...prev.filterDropdown, queryValue: value } }));
          }}
          onKeyDown={e => { if (e.key === 'Enter') apply((e.target as HTMLInputElement).value); }}
          styles={{ width: '300px', marginLeft: '12px', marginRight: '8px' }}
          input={{
            title: placeholder,
            styles: {
              height: '28px',
              paddingLeft: '4px',
              backgroundColor: 'transparent',
              borderWidth: 0,
              borderRadius: 0,
              borderBottom: `1px solid ${restLine}`,
              textOverflow: 'ellipsis',
              ':focus': { borderBottom: `2px solid ${colorScheme.brand.foreground}` }
            }
          }}
        />
      );
    }} />
  );
};
